// Hue-preserving tone map: replaces OutputPass in the composer chain.
// Per-channel ACES (the old OutputPass) compresses each channel toward 1.0
// independently, so stacked additive highlights — kill-flash clusters — lose
// their channel RATIOS and clip to a flat salmon-white. This pass instead
// evaluates the ACES fit on the max channel and scales RGB by a single
// factor, preserving hue and saturation through the highlight rolloff:
// kill-burst stacks grade red→orange→warm-white instead of fogging out.
// A gentle luma-mix above ~0.7 mapped keeps the very hottest cores reading
// warm-white (art direction) rather than laser-saturated.

import * as THREE from 'three';

export const HueTonemapShader = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    // Mirrors renderer.toneMappingExposure (hit/death pulse), applied to the
    // linear HDR input before the curve — same contract OutputPass had.
    exposure: { value: 1.0 }
  },

  vertexShader: /* glsl */ `
    varying vec2 vUv;

    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float exposure;
    varying vec2 vUv;

    // Narkowicz ACES fit, scalar — same curve family OutputPass applied
    // per-channel; here it only sets the highlight shoulder on the max
    // channel so channel ratios (hue) survive.
    float aces(float x) {
      return clamp((x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14), 0.0, 1.0);
    }

    vec3 toSrgb(vec3 c) {
      vec3 lo = c * 12.92;
      vec3 hi = 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055;
      return mix(lo, hi, step(vec3(0.0031308), c));
    }

    void main() {
      vec3 hdr = texture2D(tDiffuse, vUv).rgb * exposure;
      float m = max(hdr.r, max(hdr.g, hdr.b));
      float mapped = aces(m);
      // Ratio-preserving scale: hue/saturation intact at any intensity.
      vec3 col = m > 1e-6 ? hdr * (mapped / m) : vec3(0.0);
      // Warm-white drift only at the hottest cores (stacked kill flashes);
      // mid highlights and sprite bodies keep full chroma.
      float luma = dot(col, vec3(0.299, 0.587, 0.114));
      float desat = smoothstep(0.72, 0.95, mapped) * 0.55;
      col = mix(col, vec3(luma), desat);
      gl_FragColor = vec4(toSrgb(clamp(col, 0.0, 1.0)), 1.0);
    }
  `
};
